/**
 * Recover a tab whose lazy chunks were replaced by a release.
 *
 * (Deploy-disruption fix B. The website has had this since TSFS-FRONTEND-6/-7;
 * the admin had none.)
 *
 * THE FAILURE THIS FIXES
 * Every admin page is a separate webpack chunk, named with a content hash. A
 * release publishes a new set of names and the old files stop existing (they
 * answer 404). An admin tab left open since before the release holds a page
 * that points at names that are gone: the next click on a menu item throws
 *
 *   ChunkLoadError: Loading chunk 482 failed.
 *   (missing: https://admin.sfsops.net/static/js/482.1f3a9c0b.chunk.js)
 *
 * and, with no recovery, the whole admin was replaced by "Something went
 * wrong" with that raw text. "Try Again" could not help: React.lazy keeps the
 * failed import, so it only ever failed again. One reload fixes it completely:
 * the browser fetches the new index and the new names.
 *
 * WHY THIS IS SAFE AGAINST A RELOAD LOOP
 * The dangerous version reloads on every failure. If a chunk is genuinely
 * missing (a broken build, a blocked request), reloading reproduces the same
 * failure and the tab spins for ever. So a reload is attempted AT MOST ONCE
 * per cooldown window, recorded in sessionStorage: it is per tab and it
 * survives the reload itself, which an in-memory flag cannot. If the failure
 * outlives the reload, we stop and let the screen say so.
 *
 * sessionStorage is wrapped: in embedded browsers and private mode touching it
 * can throw SecurityError. With no storage there is no loop protection, so we
 * decline to reload rather than risk an unguardable loop; the screen then asks
 * for a manual reload.
 */

const RELOAD_KEY = 'tsfs.admin.staleChunkReloadAt'

/** Long enough to outlast one release's propagation, short enough to retry later. */
export const RELOAD_COOLDOWN_MS = 60_000

/** True between deciding to reload and the navigation actually committing. */
let reloadInFlight = false

/**
 * Whether a stale-chunk reload is on its way. Errors raised in that window are
 * the reload's own wake (the module graph is half-dead by design), not defects.
 */
export function isStaleChunkReloadInFlight(): boolean {
    return reloadInFlight
}

/** Test seam: a module-level flag would otherwise leak between cases. */
export function resetStaleChunkReloadInFlight(): void {
    reloadInFlight = false
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

export interface StaleChunkDeps {
    storage?: StorageLike | null
    reload?: () => void
    now?: () => number
}

function safeStorage(): StorageLike | null {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null
    } catch {
        // SecurityError: embedded or private browsing. The caller treats this
        // as "cannot track attempts", and therefore does not reload.
        return null
    }
}

const CHUNK_ERROR_PATTERNS = [
    /Loading (CSS )?chunk [\w-]+ failed/i, // webpack 5 (this admin)
    /Failed to fetch dynamically imported module/i, // Chrome, Edge
    /Importing a module script failed/i, // Safari, iOS
    /error loading dynamically imported module/i, // Firefox
]

/** Whether an error is a page file that a release replaced, not a bug. */
export function isChunkLoadError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false
    const { name, message } = error as { name?: unknown; message?: unknown }
    if (name === 'ChunkLoadError') return true
    if (typeof message !== 'string') return false
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

/**
 * Decide whether to reload, and record the attempt.
 *
 * Exported for tests: the value of this module is in the negative cases
 * (already tried, no storage).
 */
export function shouldReloadForStaleChunk(deps: StaleChunkDeps = {}): boolean {
    const storage = deps.storage === undefined ? safeStorage() : deps.storage
    const now = deps.now ? deps.now() : Date.now()

    if (!storage) return false

    try {
        const last = Number(storage.getItem(RELOAD_KEY) || 0)
        if (last && now - last < RELOAD_COOLDOWN_MS) return false
        storage.setItem(RELOAD_KEY, String(now))
        return true
    } catch {
        return false
    }
}

/**
 * Reload to pick up the new release, through the loop guard. Returns whether
 * it reloaded.
 */
export function reloadForNewVersion(deps: StaleChunkDeps = {}): boolean {
    if (!shouldReloadForStaleChunk(deps)) return false
    reloadInFlight = true
    ;(deps.reload ?? (() => window.location.reload()))()
    return true
}

/**
 * Recover from a failed chunk that nothing else catches (an import() whose
 * caller does not handle the rejection). A lazy PAGE is caught by
 * AppErrorBoundary, which uses the same guard.
 *
 * Returns a cleanup function.
 */
export function installStaleChunkRecovery(deps: StaleChunkDeps = {}): () => void {
    if (typeof window === 'undefined') return () => undefined

    const onUnhandledRejection = (event: Event) => {
        const reason = (event as unknown as { reason?: unknown }).reason
        if (!isChunkLoadError(reason)) return
        // A second failure inside the window is not a stale chunk: let it
        // through, swallowing it would hide a really broken build.
        if (reloadForNewVersion(deps)) event.preventDefault()
    }

    window.addEventListener('unhandledrejection', onUnhandledRejection)
    return () => window.removeEventListener('unhandledrejection', onUnhandledRejection)
}
